import React, { useEffect, useState } from 'react';
import { trackChristmasCardClick } from '../utils/analytics';
import { APP_NAME } from '../config/branding';

interface Snowflake {
  id: number;
  left: number;
  animationDuration: number;
  animationDelay: number;
  size: number;
  opacity: number;
}

const ChristmasCard: React.FC = () => {
  const [snowflakes, setSnowflakes] = useState<Snowflake[]>([]);

  useEffect(() => {
    // Generate snowflakes
    const flakes: Snowflake[] = [];
    for (let i = 0; i < 80; i++) {
      flakes.push({
        id: i,
        left: Math.random() * 100,
        animationDuration: 8 + Math.random() * 12,
        animationDelay: Math.random() * -20,
        size: 20 + Math.random() * 40,
        opacity: 0.4 + Math.random() * 0.6,
      });
    }
    setSnowflakes(flakes);
  }, []);

  return (
    <div className="christmas-card">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Mountains+of+Christmas:wght@400;700&family=Crimson+Pro:wght@300;400;600&family=Shadows+Into+Light&display=swap');
        
        .christmas-card {
          min-height: 100vh;
          background: linear-gradient(180deg, 
            #0a1628 0%, 
            #1a3a5c 30%, 
            #2d5a7b 60%, 
            #1a4a6a 100%
          );
          position: relative;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          font-family: 'Crimson Pro', Georgia, serif;
        }

        /* Northern lights effect */
        .aurora {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 60%;
          background: 
            radial-gradient(ellipse at 20% 20%, rgba(100, 200, 150, 0.15) 0%, transparent 50%),
            radial-gradient(ellipse at 80% 30%, rgba(80, 150, 200, 0.12) 0%, transparent 50%),
            radial-gradient(ellipse at 50% 10%, rgba(150, 100, 180, 0.1) 0%, transparent 40%);
          animation: aurora-shift 15s ease-in-out infinite;
          pointer-events: none;
        }

        @keyframes aurora-shift {
          0%, 100% { opacity: 0.8; transform: translateX(0); }
          50% { opacity: 1; transform: translateX(20px); }
        }

        /* Stars twinkling */
        .stars {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          pointer-events: none;
        }

        .star {
          position: absolute;
          width: 3px;
          height: 3px;
          background: white;
          border-radius: 50%;
          animation: twinkle 3s ease-in-out infinite;
        }

        @keyframes twinkle {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.3); }
        }

        /* Snowflakes */
        .snowflake {
          position: absolute;
          top: -60px;
          color: white;
          pointer-events: none;
          animation: fall linear infinite;
          text-shadow: 0 0 5px rgba(255, 255, 255, 0.5);
          z-index: 5;
        }

        @keyframes fall {
          0% {
            transform: translateY(-10vh) rotate(0deg);
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
          }
        }

        /* Snow accumulation at bottom */
        .snow-ground {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 0;
          background: linear-gradient(180deg, 
            rgba(255, 255, 255, 0.9) 0%, 
            rgba(240, 248, 255, 1) 50%,
            rgba(230, 240, 250, 1) 100%
          );
          animation: accumulate 30s ease-out forwards;
          z-index: 2;
          border-radius: 100% 100% 0 0 / 20px 20px 0 0;
        }

        .snow-ground::before {
          content: '';
          position: absolute;
          top: -10px;
          left: 0;
          right: 0;
          height: 20px;
          background: radial-gradient(ellipse at 10% 100%, rgba(255,255,255,0.8) 0%, transparent 50%),
                      radial-gradient(ellipse at 30% 100%, rgba(255,255,255,0.9) 0%, transparent 40%),
                      radial-gradient(ellipse at 50% 100%, rgba(255,255,255,0.85) 0%, transparent 45%),
                      radial-gradient(ellipse at 70% 100%, rgba(255,255,255,0.9) 0%, transparent 40%),
                      radial-gradient(ellipse at 90% 100%, rgba(255,255,255,0.8) 0%, transparent 50%);
        }

        @keyframes accumulate {
          0% {
            height: 0;
          }
          100% {
            height: 80px;
          }
        }

        /* Main content card */
        .card-content {
          position: relative;
          z-index: 10;
          text-align: center;
          padding: 2rem;
          max-width: 600px;
        }

        /* Logo container */
        .logo-container {
          position: relative;
          margin-bottom: 2rem;
          animation: float 4s ease-in-out infinite;
          display: flex;
          justify-content: center;
          align-items: center;
        }

        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }

        .logo-image {
          position: relative;
          width: 350px;
          height: 350px;
          object-fit: contain;
        }

        /* Isometric gift */
        .gift-container {
          position: relative;
          margin: 2rem 0;
          perspective: 1000px;
        }

        .gift {
          width: 120px;
          height: 120px;
          position: relative;
          transform-style: preserve-3d;
          animation: gift-bounce 2s ease-in-out infinite;
          margin: 0 auto;
        }

        @keyframes gift-bounce {
          0%, 100% { transform: translateY(0) rotateY(0deg); }
          25% { transform: translateY(-15px) rotateY(5deg); }
          75% { transform: translateY(-5px) rotateY(-5deg); }
        }

        .gift-box {
          position: absolute;
          width: 100%;
          height: 80%;
          bottom: 0;
          background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
          border-radius: 8px;
          box-shadow: 
            inset 5px 5px 15px rgba(255, 255, 255, 0.2),
            inset -5px -5px 15px rgba(0, 0, 0, 0.2),
            0 20px 40px rgba(0, 0, 0, 0.3);
        }

        .gift-box::before {
          content: '';
          position: absolute;
          top: 50%;
          left: 0;
          right: 0;
          height: 20px;
          background: linear-gradient(90deg, #fbbf24, #f59e0b, #fbbf24);
          transform: translateY(-50%);
          box-shadow: 0 2px 10px rgba(251, 191, 36, 0.5);
        }

        .gift-box::after {
          content: '';
          position: absolute;
          top: 0;
          bottom: 0;
          left: 50%;
          width: 20px;
          background: linear-gradient(180deg, #fbbf24, #f59e0b, #fbbf24);
          transform: translateX(-50%);
          box-shadow: 0 2px 10px rgba(251, 191, 36, 0.5);
        }

        .gift-lid {
          position: absolute;
          width: 110%;
          height: 25%;
          top: 0;
          left: -5%;
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          border-radius: 8px 8px 4px 4px;
          box-shadow: 
            inset 3px 3px 10px rgba(255, 255, 255, 0.2),
            0 5px 15px rgba(0, 0, 0, 0.2);
        }

        .gift-lid::before {
          content: '';
          position: absolute;
          top: 50%;
          left: 0;
          right: 0;
          height: 12px;
          background: linear-gradient(90deg, #fbbf24, #f59e0b, #fbbf24);
          transform: translateY(-50%);
        }

        .bow {
          position: absolute;
          top: -25px;
          left: 50%;
          transform: translateX(-50%);
          width: 50px;
          height: 30px;
        }

        .bow::before,
        .bow::after {
          content: '';
          position: absolute;
          width: 30px;
          height: 25px;
          background: linear-gradient(135deg, #fbbf24, #f59e0b);
          border-radius: 50% 50% 50% 50% / 60% 60% 40% 40%;
          box-shadow: inset 2px 2px 5px rgba(255, 255, 255, 0.3);
        }

        .bow::before {
          left: 0;
          transform: rotate(-30deg);
        }

        .bow::after {
          right: 0;
          transform: rotate(30deg);
        }

        .bow-center {
          position: absolute;
          top: 5px;
          left: 50%;
          transform: translateX(-50%);
          width: 16px;
          height: 16px;
          background: linear-gradient(135deg, #fbbf24, #d97706);
          border-radius: 50%;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
          z-index: 1;
        }

        /* Typography */
        .title {
          font-family: 'Shadows Into Light', cursive;
          font-size: 4rem;
          color: #ffffff;
          text-shadow: 
            0 0 40px rgba(220, 38, 38, 0.8),
            0 0 80px rgba(220, 38, 38, 0.5),
            0 4px 20px rgba(0, 0, 0, 0.8);
          margin-bottom: 0.75rem;
          letter-spacing: 3px;
        }

        .subtitle {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          font-size: 1.1rem;
          color: rgba(255, 255, 255, 0.9);
          font-weight: 600;
          letter-spacing: 6px;
          text-transform: uppercase;
          margin-bottom: 2rem;
          text-shadow: 0 2px 20px rgba(0, 0, 0, 0.8);
        }

        .message {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          font-size: 1.1rem;
          color: #ffffff;
          line-height: 1.8;
          max-width: 450px;
          margin: 0 auto 2rem;
          font-weight: 400;
          font-style: italic;
          text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
        }

        .pims-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.75rem;
          background: #16a34a;
          color: #ffffff !important;
          padding: 1rem 2rem;
          border-radius: 8px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          font-size: 1.1rem;
          font-weight: 600;
          text-decoration: none;
          transition: all 0.2s ease;
        }

        .pims-badge:hover {
          background: #15803d;
        }

        .pims-badge .icon {
          font-size: 1.25rem;
        }

        .from-section {
          margin-top: 3rem;
          padding-top: 2rem;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
          position: relative;
          z-index: 5;
        }

        .from-text {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          font-size: 0.9rem;
          color: #c4d9e8;
          letter-spacing: 3px;
          font-weight: 500;
        }

        .company-name {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          font-size: 1.8rem;
          font-weight: 700;
          color: #ffffff;
          margin-top: 0.5rem;
          letter-spacing: 1px;
          animation: text-to-dark 30s ease-out forwards;
          position: relative;
          z-index: 5;
        }

        @keyframes text-to-dark {
          0% {
            color: #ffffff;
            text-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
          }
          70% {
            color: #ffffff;
            text-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
          }
          100% {
            color: #1a3a5c;
            text-shadow: none;
          }
        }

        /* Decorative ornaments */
        .ornament {
          position: absolute;
          border-radius: 50%;
          box-shadow: 
            inset 5px 5px 15px rgba(255, 255, 255, 0.3),
            0 5px 15px rgba(0, 0, 0, 0.2);
        }

        .ornament-1 {
          width: 40px;
          height: 40px;
          background: linear-gradient(135deg, #dc2626, #991b1b);
          top: 15%;
          left: 10%;
          animation: swing 4s ease-in-out infinite;
        }

        .ornament-2 {
          width: 30px;
          height: 30px;
          background: linear-gradient(135deg, #16a34a, #15803d);
          top: 25%;
          right: 15%;
          animation: swing 5s ease-in-out infinite 0.5s;
        }

        .ornament-3 {
          width: 35px;
          height: 35px;
          background: linear-gradient(135deg, #fbbf24, #d97706);
          bottom: 20%;
          left: 8%;
          animation: swing 4.5s ease-in-out infinite 1s;
        }

        .ornament-4 {
          width: 25px;
          height: 25px;
          background: linear-gradient(135deg, #2563eb, #1d4ed8);
          bottom: 30%;
          right: 12%;
          animation: swing 5.5s ease-in-out infinite 0.3s;
        }

        @keyframes swing {
          0%, 100% { transform: rotate(-5deg); }
          50% { transform: rotate(5deg); }
        }

        .ornament::before {
          content: '';
          position: absolute;
          top: -8px;
          left: 50%;
          transform: translateX(-50%);
          width: 8px;
          height: 8px;
          background: #fbbf24;
          border-radius: 2px;
        }

        .ornament::after {
          content: '';
          position: absolute;
          top: -15px;
          left: 50%;
          transform: translateX(-50%);
          width: 2px;
          height: 10px;
          background: #888;
        }

        /* Holly decoration */
        .holly {
          position: absolute;
          bottom: 5%;
          right: 5%;
          font-size: 3rem;
          animation: holly-bounce 3s ease-in-out infinite;
        }

        @keyframes holly-bounce {
          0%, 100% { transform: rotate(-5deg); }
          50% { transform: rotate(5deg); }
        }

        @media (max-width: 640px) {
          .title {
            font-size: 2.5rem;
          }
          
          .logo-image {
            width: 280px;
            height: 280px;
          }
          
          .pims-badge {
            font-size: 1.2rem;
            padding: 0.6rem 1.5rem;
          }
        }

      `}</style>

      {/* Aurora effect */}
      <div className="aurora" />

      {/* Main content */}
      <div className="card-content">
        <h1 className="title">Merry Christmas!</h1>
        <p className="subtitle">Season's Greetings 2025</p>

        {/* Logo */}
        <div className="logo-container">
          <img
            src="/xmass.png"
            alt={`${APP_NAME} Holiday`}
            className="logo-image"
          />
        </div>

        <p className="message">
          Wishing you and your furry friends a wonderful Christmas season 
          filled with joy, treats, and wagging tails!
        </p>

        <a 
          href="https://helppet.ai" 
          className="pims-badge" 
          target="_blank" 
          rel="noopener noreferrer"
          onClick={() => trackChristmasCardClick()}
        >
          <span className="icon">🎄</span>
          <span>World's Best PIMS</span>
          <span className="icon">🎁</span>
        </a>

        <div className="from-section">
          <p className="from-text">WITH LOVE FROM</p>
          <p className="company-name">{APP_NAME}</p>
        </div>
      </div>

      {/* Stars */}
      <div className="stars">
        {[...Array(30)].map((_, i) => (
          <div
            key={`star-${i}`}
            className="star"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 50}%`,
              animationDelay: `${Math.random() * 3}s`,
            }}
          />
        ))}
      </div>

      {/* Snowflakes */}
      {snowflakes.map((flake) => (
        <div
          key={flake.id}
          className="snowflake"
          style={{
            left: `${flake.left}%`,
            fontSize: `${flake.size}px`,
            opacity: flake.opacity,
            animationDuration: `${flake.animationDuration}s`,
            animationDelay: `${flake.animationDelay}s`,
          }}
        >
          ❄
        </div>
      ))}

      {/* Ornaments */}
      <div className="ornament ornament-1" />
      <div className="ornament ornament-2" />
      <div className="ornament ornament-3" />
      <div className="ornament ornament-4" />

      {/* Holly decoration */}
      <div className="holly">🎄</div>

      {/* Snow accumulation */}
      <div className="snow-ground" />
    </div>
  );
};

export default ChristmasCard;


